import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Badge, Spinner, Button,
  Form, Input, Label, FormGroup, Alert, Table,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAuthUser } from "../../hooks/AuthProvider";
import {
  getTenantById,
  getPropertyById,
  getLeaseById,
  getAllLeasesByTenantEmail,
  getMyPropertyTickets,
  notifyPaymentReceived,
} from "../../helpers/firestoreService";
import { createRentPaymentIntent, getLeasePayments } from "../../helpers/paymentsApi";
import { paymentSummaryLabel } from "../../helpers/utils";
import PropertyChat from "../../components/Common/PropertyChat";
import InspectionsPanel from "../../components/Common/InspectionsPanel";
import MonthPicker from "../../components/Common/MonthPicker";

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (d) => {
  if (!d) return "—";
  if (d instanceof Date) return d.toLocaleDateString();
  // Plain "YYYY-MM-DD" lease dates render as-is; ISO timestamps (from the
  // getLeasePayments Lambda's JSON-safe serialization) need parsing first.
  if (typeof d === "string") return d.includes("T") ? new Date(d).toLocaleDateString() : d;
  if (d?.toDate) return d.toDate().toLocaleDateString();
  return String(d);
};

/** Sorts payment rows newest-first — paidAt/createdAt may be ISO strings
 * (getLeasePayments Lambda) or Date objects, so compare via Date parsing. */
const sortPaymentsDesc = (list) =>
  [...(list || [])].sort(
    (a, b) => new Date(b.paidAt || b.createdAt || 0) - new Date(a.paidAt || a.createdAt || 0)
  );

const money = (n) =>
  n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0 })}` : "—";

/** Human summary of a checkout's items, e.g. "security deposit and rent for
 * 2026-01, 2026-02" — used for the success message and PM notification. */
const describePaidItems = (months, includesDeposit) => {
  const parts = [];
  if (includesDeposit) parts.push("security deposit");
  if (months.length) parts.push(`rent for ${months.join(", ")}`);
  return parts.join(" and ") || "your payment";
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate(); // month is 1-based

/** Dollar rent owed for `month` ("YYYY-MM") — the full monthlyRent unless
 * the lease's startDate/endDate ("YYYY-MM-DD" strings) falls inside that
 * same calendar month, in which case it's prorated by calendar day. Mirrors
 * the backend's prorated_rent_cents in createRentPaymentIntent.py (the
 * authoritative copy that actually decides what's charged) — this is for
 * display only, so the tenant sees the right number before paying.
 */
const prorateRent = (monthlyRent, month, startDate, endDate) => {
  const rent = Number(monthlyRent) || 0;
  if (!month) return rent;
  const [year, mon] = month.split("-").map(Number);
  const totalDays = daysInMonth(year, mon);
  let firstDay = 1;
  let lastDay = totalDays;
  if (startDate) {
    const [sy, sm, sd] = startDate.split("-").map(Number);
    if (sy === year && sm === mon) firstDay = sd;
  }
  if (endDate) {
    const [ey, em, ed] = endDate.split("-").map(Number);
    if (ey === year && em === mon) lastDay = ed;
  }
  if (firstDay <= 1 && lastDay >= totalDays) return rent;
  if (firstDay > lastDay) return 0;
  const applicableDays = lastDay - firstDay + 1;
  return Math.round(((rent * applicableDays) / totalDays) * 100) / 100;
};

const TICKET_STATUS_COLOR = { Open: "primary", "In Progress": "warning", Done: "success", Resolved: "success", Closed: "secondary" };
const SEVERITY_COLOR = { Low: "info", Medium: "warning", High: "orange", Urgent: "danger" };

// Manual (PM-recorded, off-platform) payments have no `status` field —
// they represent money already collected, so treat a missing status as "paid".
// No "pending" entry — nothing is written until stripeWebhook reports a real
// processing/paid/failed event, so that status never actually occurs.
const PAYMENT_STATUS_BADGE = {
  processing: { label: "Processing", color: "warning" },
  paid: { label: "Paid", color: "success" },
  failed: { label: "Failed", color: "danger" },
};

// Must match the fees quoted on the public pricing page. The tenant picks
// one of these up front so the fee (and the Stripe payment_method_type the
// Lambda restricts the PaymentIntent to) is locked in before checkout —
// they can no longer switch methods after seeing a lower quoted amount.
const RENT_PAYMENT_METHODS = {
  ach: { label: "Bank account (ACH)", icon: "bx-transfer-alt", rate: 0.0075, feeLabel: "0.75%" },
  card: { label: "Credit / debit card", icon: "bx-credit-card", rate: 0.0375, feeLabel: "3.75%" },
};

/** Renders inside <Elements> — needs useStripe/useElements, so it can't live
 * in the parent component, which mounts before the client secret exists. */
const RentPaymentForm = ({ amountLabel, payerEmail, onSuccess, onError, onSubmittingChange }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    onSubmittingChange(true);
    onError("");
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        onError(error.message || "Payment failed. Please try again.");
        return;
      }
      if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
        onSuccess();
      } else {
        onError("Payment could not be completed. Please try a different payment method.");
      }
    } finally {
      setSubmitting(false);
      onSubmittingChange(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {/* Prefilling the email scopes Stripe Link's autofill/save-card
       * prompt to this tenant's identity instead of whichever email Link
       * last recognized in this browser (e.g. a spouse sharing the device). */}
      <PaymentElement options={{ defaultValues: { billingDetails: { email: payerEmail || "" } } }} />
      <div className="text-muted d-flex align-items-center gap-1 mt-3" style={{ fontSize: "0.7rem" }}>
        <i className="bx bx-lock-alt" />
        Payments are processed securely by Stripe.
      </div>
      <Button color="primary" className="w-100 mt-3" type="submit" disabled={!stripe || submitting}>
        {submitting ? <Spinner size="sm" /> : `Pay ${amountLabel}`}
      </Button>
    </Form>
  );
};

/** Returns the next rent due Date given the setup's rentDueDay and firstPaymentDate. */
const getNextDueDate = (plan) => {
  if (!plan?.rentDueDay) return null;
  const dueDay = parseInt(plan.rentDueDay) || 1;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const y = today.getFullYear();
  const m = today.getMonth();
  const thisMonthDue = new Date(y, m, dueDay);
  let next = thisMonthDue >= today ? thisMonthDue : new Date(y, m + 1, dueDay);
  if (plan.firstPaymentDate) {
    const first = new Date(plan.firstPaymentDate + "T00:00:00");
    if (first >= today && first < next) next = first;
  }
  return next;
};

// ─────────────────────────────────────────────────────────────────────────────

const TenantRentalDetail = () => {
  document.title = "My Rental | Porcher";

  const { tenantRecordId } = useParams();
  const navigate = useNavigate();
  const user = useAuthUser();
  const [searchParams] = useSearchParams();

  const [tenantRecord, setTenantRecord] = useState(null);
  const [property, setProperty] = useState(null);
  const [rentPlan, setRentPlan] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");

  // Payment modal
  const [payModal, setPayModal] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [payMonths, setPayMonths] = useState([""]); // one row per rent month being checked out
  const [payDeposit, setPayDeposit] = useState(false); // whether the security deposit is included
  const [payMethod, setPayMethod] = useState(""); // "ach" | "card" — chosen before the intent is created
  const [clientSecret, setClientSecret] = useState("");
  const [payAmountCents, setPayAmountCents] = useState(null);
  const [payIntentLoading, setPayIntentLoading] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  const [payError, setPayError] = useState("");
  const [paySuccess, setPaySuccess] = useState(false);

  // Which rent months / whether the deposit are already paid — checked
  // against both itemized (new, combined-checkout) and legacy single-month
  // payment docs, so a tenant can't double-pay something already settled.
  const paidMonths = useMemo(() => {
    const set = new Set();
    payments.forEach((p) => {
      if (Array.isArray(p.items) && p.items.length) {
        p.items.forEach((it) => { if (it.type === "rent" && it.month) set.add(it.month); });
      } else if (p.month) {
        set.add(p.month);
      }
    });
    return set;
  }, [payments]);

  const depositPaid = useMemo(
    () => payments.some((p) => (Array.isArray(p.items) && p.items.some((it) => it.type === "deposit")) || p.type === "deposit"),
    [payments]
  );

  // Creates (or recreates, as the tenant edits the cart) the PaymentIntent
  // backing the Payment Element below — never fires until a method is
  // chosen, so the fee-inclusive amount and payment_method_types are locked
  // together. Bundles the deposit + every selected month into one
  // PaymentIntent so a combined checkout only incurs one processing fee.
  useEffect(() => {
    if (!payModal || !payMethod || !rentPlan?.id) return;
    const validMonths = [...new Set(payMonths.filter(Boolean))];
    if (!payDeposit && validMonths.length === 0) return;
    let cancelled = false;
    setClientSecret("");
    setPayIntentLoading(true);
    setPayError("");
    const items = [
      ...(payDeposit ? [{ type: "deposit" }] : []),
      ...validMonths.map((month) => ({ type: "rent", month })),
    ];
    (async () => {
      try {
        const { clientSecret: cs, amount } = await createRentPaymentIntent({ leaseId: rentPlan.id, items, paymentMethod: payMethod });
        if (!cancelled) {
          setClientSecret(cs);
          setPayAmountCents(typeof amount === "number" ? amount : null);
        }
      } catch (err) {
        console.error("Create rent payment intent error:", err);
        if (!cancelled) setPayError(err.message || "Couldn't start payment. Please try again.");
      } finally {
        if (!cancelled) setPayIntentLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // payMonths is an array — re-run when its (joined) contents change, not just its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payModal, payMethod, rentPlan?.id, payDeposit, payMonths.join(",")]);

  useEffect(() => {
    if (!tenantRecordId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const tr = await getTenantById(tenantRecordId);
        if (!tr || cancelled) return;
        setTenantRecord(tr);

        const [propRes, planRes] = await Promise.allSettled([
          tr.propertyId ? getPropertyById(tr.propertyId) : Promise.resolve(null),
          // Primary: direct document read via the id stamped on the tenant record
          // Fallback: email-based collection query for setups created before stamping
          tr.leaseId
            ? getLeaseById(tr.leaseId)
            : (user?.email ? getAllLeasesByTenantEmail(user.email) : Promise.resolve([])),
        ]);
        if (cancelled) return;
        const prop = propRes.status === "fulfilled" ? propRes.value : null;
        const planResult = planRes.status === "fulfilled" ? planRes.value : null;
        // planResult is a single doc (direct read) or an array (email query)
        let activePlan = null;
        if (Array.isArray(planResult)) {
          activePlan = planResult.find(
            (l) => l.propertyId === tr.propertyId && l.status === "Active" && !l.deleted
          ) || planResult.find((l) => l.propertyId === tr.propertyId && !l.deleted) || null;
        } else if (planResult) {
          activePlan = planResult;
        }
        setProperty(prop);
        setRentPlan(activePlan);
        // Derive owner contacts from the setup — avoids a separate query tenants can't run
        const ll = activePlan
          ? (activePlan.landlordNames || []).map((name, i) => ({
              id: `ll-${i}`,
              name,
              email: activePlan.landlordEmails?.[i] || "",
              phone: "",
            }))
          : [];
        setLandlords(ll);
      } catch (e) {
        console.error("TenantRentalDetail load error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantRecordId, user?.uid]);

  // Payment history loads independently of the rest of the page — it hits a
  // separate Lambda (getLeasePayments, subject to cold starts) and shouldn't
  // block rendering the property/lease info tenants came here for.
  useEffect(() => {
    if (!rentPlan?.id || !user?.email) { setPaymentsLoading(false); return; }
    let cancelled = false;
    setPaymentsLoading(true);
    (async () => {
      try {
        const { payments: pmts } = await getLeasePayments(rentPlan.id);
        if (!cancelled) setPayments(sortPaymentsDesc(pmts));
      } catch (e) {
        console.warn("Payments load error:", e);
      } finally {
        if (!cancelled) setPaymentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [rentPlan?.id, user?.email]);

  // Same idea for tickets — its own request, its own loading state.
  useEffect(() => {
    if (!user?.uid) { setTicketsLoading(false); return; }
    let cancelled = false;
    setTicketsLoading(true);
    (async () => {
      try {
        const myTickets = await getMyPropertyTickets(user.uid);
        if (!cancelled) setTickets(myTickets.filter((t) => !tenantRecord?.propertyId || t.propertyId === tenantRecord.propertyId));
      } catch (e) {
        console.warn("Tickets load error:", e);
      } finally {
        if (!cancelled) setTicketsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid, tenantRecord?.propertyId]);

  if (loading) {
    return (
      <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (!tenantRecord) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5 text-muted">
            <i className="bx bx-home-alt" style={{ fontSize: "2.5rem" }} />
            <p className="mt-2">Rental not found.</p>
            <Button color="primary" outline size="sm" onClick={() => navigate("/tenant/my-rentals")}>
              Back to My Rentals
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const myEmail = (user?.email || "").toLowerCase();

  const validMonths = [...new Set(payMonths.filter(Boolean))].sort();
  const depositAmount = Number(rentPlan?.securityDeposit) || 0;
  const rentAmount = Number(rentPlan?.monthlyRent) || 0;
  const monthRent = (month) => prorateRent(rentAmount, month, rentPlan?.startDate, rentPlan?.endDate);
  // The service fee only applies to rent — the security deposit is never fee'd.
  const rentSubtotal = validMonths.reduce((sum, month) => sum + monthRent(month), 0);
  const subtotal = (payDeposit ? depositAmount : 0) + rentSubtotal;
  const hasConflict = payMonths.some((m) => m && paidMonths.has(m)) || payMonths.filter(Boolean).length !== validMonths.length;
  const canCheckout = (payDeposit || validMonths.length > 0) && !hasConflict;

  const handleOpenPayModal = () => {
    // Default to a single row for the current month if not yet paid, otherwise next month
    const today = new Date();
    const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const nextMonthStr = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, "0")}`;
    setPayMonths([paidMonths.has(currentMonth) ? nextMonthStr : currentMonth]);
    // Nudge tenants who still owe the deposit by pre-selecting it for them.
    setPayDeposit(depositAmount > 0 && !depositPaid);
    setPayMethod("");
    setClientSecret("");
    setPayAmountCents(null);
    setPayError("");
    setPaySuccess(false);
    setPayModal(true);
  };

  // Called once Stripe confirms the PaymentIntent succeeded client-side. The
  // `payments` Firestore doc is only written once stripeWebhook.py receives
  // a real Stripe event (processing/succeeded/payment_failed) for this
  // intent — nothing is written eagerly, so an abandoned checkout never
  // leaves a stray row. That means the row doesn't exist yet at this point
  // either, so poll a few times instead of a single fixed-delay refetch
  // (which was often too early).
  const handlePaySuccess = () => {
    setPaySuccess(true);
    notifyPaymentReceived({
      pmUid: rentPlan.createdBy,
      propertyId: rentPlan.propertyId,
      tenantName: user.displayName || user.email,
      amount: subtotal,
      description: describePaidItems(validMonths, payDeposit),
    }).catch((e) => console.error("Notify payment received error:", e));

    const intentId = clientSecret ? clientSecret.split("_secret_")[0] : null;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { payments: fresh } = await getLeasePayments(rentPlan.id);
        const landed = !intentId || fresh.some((p) => p.id === intentId);
        if (landed || attempts >= 6) {
          setPayments(sortPaymentsDesc(fresh));
          return;
        }
      } catch (e) {
        console.warn("Refresh payments error:", e);
      }
      setTimeout(poll, 2000);
    };
    setTimeout(poll, 1500);
  };

  const tabs = [
    { id: "overview", icon: "bx-home",         label: "Overview" },
    { id: "rent",     icon: "bx-credit-card",   label: "Rent & Payments" },
    { id: "inspections", icon: "bx-clipboard",  label: "Inspections" },
    { id: "chat",     icon: "bx-conversation",  label: "Chat" },
    { id: "support",  icon: "bx-support",       label: "Support" },
  ];

  return (
    <div className="page-content">
      <Container fluid style={{ maxWidth: 1000 }}>

        {/* Back link */}
        <div className="mb-3">
          <Button color="link" className="p-0 text-muted small" onClick={() => navigate("/tenant/my-rentals")}>
            <i className="bx bx-arrow-back me-1" />Back to My Rentals
          </Button>
        </div>

        {/* Hero */}
        <div className="rounded-3 mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 110 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative d-flex justify-content-between align-items-start flex-wrap gap-3" style={{ zIndex: 1 }}>
            <div>
              <h5 className="mb-1 text-white fw-bold">
                <i className="bx bx-map-pin me-2" />
                {property?.address || "Loading address…"}
              </h5>
              <div className="opacity-75 small">
                {tenantRecord.name && (
                  <span className="me-3">
                    <i className="bx bx-user me-1" />{tenantRecord.name}
                  </span>
                )}
                {rentPlan && (
                  <span>
                    <i className="bx bx-calendar me-1" />
                    {fmt(rentPlan.startDate)} → {fmt(rentPlan.endDate)}
                  </span>
                )}
              </div>
            </div>
            {rentPlan && (
              <div className="text-end">
                <div className="fs-4 fw-bold text-white">
                  {money(rentPlan.monthlyRent)}<span className="fs-6 opacity-75">/mo</span>
                </div>
                <Badge color={rentPlan.status === "Active" ? "success" : "secondary"} pill className="mt-1">
                  {rentPlan.status || "Active"}
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Nav tabs className="mb-0">
          {tabs.map((t) => (
            <NavItem key={t.id}>
              <NavLink
                className={activeTab === t.id ? "active" : ""}
                style={{ cursor: "pointer" }}
                onClick={() => setActiveTab(t.id)}
              >
                <i className={`bx ${t.icon} me-1`} />{t.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        <TabContent activeTab={activeTab} className="pt-4">

          {/* ── Overview ─────────────────────────────────────────── */}
          <TabPane tabId="overview">
            <Row className="g-3">
              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                      <i className="bx bx-building-house me-1" />Property Info
                    </h6>
                    <Table borderless size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <td className="text-muted small" style={{ width: "40%" }}>Address</td>
                          <td className="small fw-medium">{property?.address || "—"}</td>
                        </tr>
                        {property?.unitNumber && (
                          <tr>
                            <td className="text-muted small">Unit</td>
                            <td className="small">{property.unitNumber}</td>
                          </tr>
                        )}
                        {property?.propertyType && (
                          <tr>
                            <td className="text-muted small">Type</td>
                            <td className="small text-capitalize">{property.propertyType}</td>
                          </tr>
                        )}
                        {property?.bedrooms != null && (
                          <tr>
                            <td className="text-muted small">Bedrooms</td>
                            <td className="small">{property.bedrooms}</td>
                          </tr>
                        )}
                        {property?.bathrooms != null && (
                          <tr>
                            <td className="text-muted small">Bathrooms</td>
                            <td className="small">{property.bathrooms}</td>
                          </tr>
                        )}
                        {property?.squareFeet && (
                          <tr>
                            <td className="text-muted small">Sq Ft</td>
                            <td className="small">{property.squareFeet}</td>
                          </tr>
                        )}
                        {property?.notes && (
                          <tr>
                            <td className="text-muted small">Notes</td>
                            <td className="small">{property.notes}</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                      <i className="bx bx-user me-1" />Contacts
                    </h6>
                    {landlords.length === 0 ? (
                      <p className="text-muted small mb-0">No landlord contacts on file.</p>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {landlords.map((l) => (
                          <div key={l.id} className="d-flex align-items-center gap-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: 36, height: 36, background: "#eff2ff" }}
                            >
                              <i className="bx bx-user-circle text-primary" style={{ fontSize: "1.2rem" }} />
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="fw-semibold small">{l.name || "Landlord"}</div>
                              {l.email && (
                                <a href={`mailto:${l.email}`} className="text-muted small d-block text-truncate">
                                  {l.email}
                                </a>
                              )}
                              {l.phone && <div className="text-muted small">{l.phone}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col xs={12}>
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                      <i className="bx bx-lightning-bolt me-1" />Quick Actions
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      <Button color="success" size="sm" outline onClick={() => setActiveTab("rent")}>
                        <i className="bx bx-credit-card me-1" />Pay Rent
                      </Button>
                      <Button color="primary" size="sm" outline onClick={() => setActiveTab("chat")}>
                        <i className="bx bx-conversation me-1" />Message Owner
                      </Button>
                      <Button color="warning" size="sm" outline onClick={() => navigate("/tenant/tickets")}>
                        <i className="bx bx-support me-1" />Submit Ticket
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* ── Rent & Payments ──────────────────────────────────── */}
          <TabPane tabId="rent">
            {!rentPlan && (
              <Alert color="light" className="border d-flex align-items-center gap-2 mb-3">
                <i className="bx bx-info-circle fs-5 text-muted" />
                <div className="small">
                  <strong>Rent payments aren't set up yet.</strong>{" "}
                  Your property owner needs to configure rent collection before you can pay online.
                </div>
              </Alert>
            )}
            <Row className="g-3">
              {rentPlan && (
                <Col md={5}>
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody>
                      <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                        <i className="bx bx-dollar-circle me-1" />Current Rent
                      </h6>
                      <div className="text-center py-3">
                        <div className="display-6 fw-bold text-primary">{money(rentPlan.monthlyRent)}</div>
                        <div className="text-muted small mt-1">per month</div>
                        {rentPlan.rentDueDay && (
                          <div className="text-muted small mt-2">
                            <i className="bx bx-calendar me-1" />
                            Due on the {rentPlan.rentDueDay}{["st","nd","rd"][(((rentPlan.rentDueDay%100-11)%10)-1+3)%3] || "th"} of each month
                          </div>
                        )}
                        {(() => {
                          const nd = getNextDueDate(rentPlan);
                          return nd ? (
                            <div className="text-muted small mt-1">
                              <i className="bx bx-time me-1" />
                              Next payment: <strong>{nd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</strong>
                            </div>
                          ) : null;
                        })()}
                        {rentPlan.securityDeposit ? (
                          <div className="text-muted small mt-2">
                            <i className="bx bx-wallet me-1" />
                            Security deposit: {money(rentPlan.securityDeposit)}
                            {depositPaid && <Badge color="success" pill className="ms-1">Paid</Badge>}
                          </div>
                        ) : null}
                        {(rentPlan.startDate || rentPlan.endDate) && (
                          <div className="text-muted small mt-1">
                            <i className="bx bx-calendar-check me-1" />
                            {fmt(rentPlan.startDate)} → {fmt(rentPlan.endDate)}
                          </div>
                        )}
                      </div>
                      <div className="text-center mt-2">
                        <Button color="primary" onClick={handleOpenPayModal}>
                          <i className="bx bx-credit-card me-1" />Pay Rent
                        </Button>
                      </div>
                      {rentPlan.notes && (
                        <div className="mt-3 pt-3 border-top">
                          <div className="text-muted small fw-semibold mb-1">Notes from your property owner</div>
                          <p className="small mb-0" style={{ whiteSpace: "pre-wrap" }}>{rentPlan.notes}</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              )}
              <Col md={rentPlan ? 7 : 12}>
                <Card className="border-0 shadow-sm h-100">
                  <CardBody>
                    <h6 className="fw-semibold mb-3 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                      <i className="bx bx-receipt me-1" />Payment History
                    </h6>
                    {paymentsLoading ? (
                      <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>
                    ) : payments.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bx bx-receipt" style={{ fontSize: "2rem", opacity: 0.4 }} />
                        <p className="small mt-2 mb-0">No payments recorded yet.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table size="sm" className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Month</th>
                              <th>Amount Paid</th>
                              <th>Status</th>
                              <th>Method</th>
                              <th>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.map((p) => (
                              <tr key={p.id}>
                                <td>{paymentSummaryLabel(p)}</td>
                                <td>
                                  ${(p.totalAmount ?? p.amount ?? 0).toLocaleString()}
                                  {p.feeAmount ? (
                                    <span className="text-muted d-block" style={{ fontSize: "0.68rem" }}>
                                      incl. ${p.feeAmount.toLocaleString()} fee
                                    </span>
                                  ) : null}
                                </td>
                                <td>
                                  <Badge color={PAYMENT_STATUS_BADGE[p.status || "paid"]?.color || "secondary"} pill>
                                    {PAYMENT_STATUS_BADGE[p.status || "paid"]?.label || p.status}
                                  </Badge>
                                </td>
                                <td className="text-muted">{p.last4 ? `•••• ${p.last4}` : "—"}</td>
                                <td className="text-muted small">{fmt(p.paidAt)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* ── Inspections ──────────────────────────────────────── */}
          <TabPane tabId="inspections">
            {activeTab === "inspections" && tenantRecord?.propertyId ? (
              <InspectionsPanel
                propertyId={tenantRecord.propertyId}
                propertyAddress={property?.address || ""}
                user={user}
                canManage={false}
              />
            ) : (
              <Alert color="light" className="border small mb-0">
                Inspections will show up here once your property owner schedules one.
              </Alert>
            )}
          </TabPane>

          {/* ── Chat ─────────────────────────────────────────────── */}
          <TabPane tabId="chat">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <h6 className="fw-semibold mb-1">
                  <i className="bx bx-conversation text-primary me-2" />
                  Property Chat
                </h6>
                <p className="text-muted small mb-3">
                  Talk directly with your property owner and manager.
                </p>
                {activeTab === "chat" && tenantRecord?.propertyId && (
                  <PropertyChat
                    propertyId={tenantRecord.propertyId}
                    user={user}
                    height={400}
                    placeholder="Message your property owner…"
                  />
                )}
              </CardBody>
            </Card>
          </TabPane>

          {/* ── Support ──────────────────────────────────────────── */}
          <TabPane tabId="support">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0 text-muted text-uppercase" style={{ fontSize: "0.72rem", letterSpacing: "0.05em" }}>
                    <i className="bx bx-task me-1" />Support Tickets
                  </h6>
                  <Button color="primary" size="sm" onClick={() => navigate("/tenant/tickets")}>
                    <i className="bx bx-plus me-1" />New Ticket
                  </Button>
                </div>

                {ticketsLoading ? (
                  <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bx bx-task" style={{ fontSize: "2rem" }} />
                    <p className="mt-2 mb-0 small">No tickets yet for this property.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover align-middle mb-0" size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Subject</th>
                          <th>Urgency</th>
                          <th>Status</th>
                          <th>Submitted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((t) => (
                          <tr
                            key={t.id}
                            role="button"
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/tenant/tickets/${t.id}`)}
                          >
                            <td>
                              <div className="fw-medium small">{t.title}</div>
                              {t.description && (
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                                  {t.description.length > 80 ? t.description.slice(0, 80) + "…" : t.description}
                                </div>
                              )}
                            </td>
                            <td>
                              <Badge color={SEVERITY_COLOR[t.severity] || "secondary"} pill>
                                {t.severity || "Low"}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={TICKET_STATUS_COLOR[t.status] || "secondary"} pill>
                                {t.status}
                              </Badge>
                            </td>
                            <td className="text-muted small">{fmt(t.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </TabPane>

        </TabContent>

        {/* Pay rent modal */}
        <Modal isOpen={payModal} toggle={() => !paySubmitting && setPayModal(false)} centered>
          <ModalHeader toggle={() => !paySubmitting && setPayModal(false)}>
            <i className="bx bx-credit-card me-2 text-primary" />Pay Rent
          </ModalHeader>
          <ModalBody>
            {paySuccess ? (
              <div className="text-center py-3">
                <i className="bx bx-check-circle text-success" style={{ fontSize: "3rem" }} />
                <h6 className="mt-2 fw-semibold">Payment Submitted!</h6>
                <p className="text-muted small mb-0">
                  Your {describePaidItems(validMonths, payDeposit)} is on its way to your property owner.
                </p>
              </div>
            ) : (
              <>
                {payError && <Alert color="danger" className="py-2 small mb-3">{payError}</Alert>}

                {!payMethod ? (
                  <>
                    {depositAmount > 0 && (
                      <div className="mb-3">
                        <div
                          className="form-check d-flex align-items-center justify-content-between border rounded p-2"
                          style={{
                            cursor: depositPaid || paySubmitting ? "default" : "pointer",
                            opacity: depositPaid ? 0.6 : 1,
                            background: depositPaid ? "#f8f9fa" : undefined,
                          }}
                          onClick={() => { if (!depositPaid && !paySubmitting) setPayDeposit((v) => !v); }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <input
                              className="form-check-input mt-0"
                              type="checkbox"
                              checked={payDeposit}
                              readOnly
                              tabIndex={-1}
                              disabled={depositPaid || paySubmitting}
                            />
                            <span className="form-check-label mb-0 small fw-semibold">Security Deposit</span>
                          </div>
                          {depositPaid ? (
                            <Badge color="secondary" pill>Already paid</Badge>
                          ) : (
                            <span className="d-flex align-items-center gap-2">
                              <Badge color="success" pill className="fw-normal" style={{ fontSize: "0.62rem" }}>No fee</Badge>
                              <span className="small fw-semibold">{money(depositAmount)}</span>
                            </span>
                          )}
                        </div>
                        <div className="text-muted mt-1" style={{ fontSize: "0.7rem" }}>
                          {depositPaid
                            ? "You've already paid your security deposit."
                            : "You haven't paid your security deposit yet — it's included below by default, with no service fee."}
                        </div>
                      </div>
                    )}

                    <Label className="small fw-semibold mb-1">Rent</Label>
                    {payMonths.map((month, i) => {
                      const conflict = !!month && (paidMonths.has(month) || payMonths.filter((m) => m === month).length > 1);
                      const amount = month ? monthRent(month) : rentAmount;
                      const isProrated = !!month && amount !== rentAmount;
                      return (
                        <div key={i} className="d-flex align-items-start gap-2 mb-2">
                          <div className="flex-grow-1">
                            <MonthPicker
                              value={month}
                              invalid={conflict}
                              disabled={paySubmitting}
                              onChange={(ym) => setPayMonths((prev) => prev.map((m, idx) => (idx === i ? ym : m)))}
                            />
                            {conflict ? (
                              <div className="text-danger" style={{ fontSize: "0.7rem" }}>
                                {paidMonths.has(month) ? "Already paid" : "Duplicate month"}
                              </div>
                            ) : month && (
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                {isProrated ? `Prorated: ${money(amount)}` : money(amount)}
                              </div>
                            )}
                          </div>
                          {payMonths.length > 1 && (
                            <Button
                              color="link"
                              className="text-danger p-0 mt-2"
                              disabled={paySubmitting}
                              onClick={() => setPayMonths((prev) => prev.filter((_, idx) => idx !== i))}
                            >
                              <i className="bx bx-x fs-5" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                    <Button
                      color="link"
                      size="sm"
                      className="p-0 mb-3"
                      disabled={paySubmitting}
                      onClick={() => setPayMonths((prev) => [...prev, ""])}
                    >
                      <i className="bx bx-plus me-1" />Add another month
                    </Button>

                    <Label className="small fw-semibold mb-2 d-block">How would you like to pay?</Label>
                    <div className="d-flex flex-column gap-2 mb-2">
                      {Object.entries(RENT_PAYMENT_METHODS).map(([key, m]) => {
                        const fee = Math.round(rentSubtotal * m.rate * 100) / 100;
                        return (
                          <button
                            key={key}
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center justify-content-between text-start"
                            disabled={!canCheckout}
                            onClick={() => setPayMethod(key)}
                          >
                            <span className="d-flex align-items-center gap-2">
                              <i className={`bx ${m.icon} fs-5`} />
                              <span>
                                <span className="d-block fw-semibold">{m.label}</span>
                                <span className="d-block text-muted" style={{ fontSize: "0.72rem" }}>
                                  +{m.feeLabel} fee ({money(fee)})
                                </span>
                              </span>
                            </span>
                            <span className="fw-semibold">{money(subtotal + fee)}</span>
                          </button>
                        );
                      })}
                    </div>
                    {!canCheckout && (
                      <div className="text-muted mb-1" style={{ fontSize: "0.7rem" }}>
                        Select the security deposit and/or at least one unpaid month to continue.
                      </div>
                    )}
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      The service fee is charged to cover payment processing and is added on top of rent only
                      (the security deposit is never fee'd) — your property owner still receives the full rent/deposit amount.
                    </div>
                  </>
                ) : (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="small">
                        <div className="d-flex align-items-center gap-1 text-muted">
                          <i className={`bx ${RENT_PAYMENT_METHODS[payMethod].icon}`} />
                          Paying with {RENT_PAYMENT_METHODS[payMethod].label}
                        </div>
                      </div>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        disabled={paySubmitting}
                        onClick={() => { setPayMethod(""); setClientSecret(""); setPayAmountCents(null); }}
                      >
                        Change method
                      </Button>
                    </div>
                    {(() => {
                      const fee = Math.round(rentSubtotal * RENT_PAYMENT_METHODS[payMethod].rate * 100) / 100;
                      const total = payAmountCents != null ? payAmountCents / 100 : subtotal + fee;
                      return (
                        <div className="d-flex flex-column gap-1 mb-3 small">
                          {payDeposit && (
                            <div className="d-flex justify-content-between">
                              <span className="text-muted">Security deposit <span className="text-success">(no fee)</span></span>
                              <span>{money(depositAmount)}</span>
                            </div>
                          )}
                          {validMonths.map((month) => (
                            <div key={month} className="d-flex justify-content-between"><span className="text-muted">Rent — {month}{monthRent(month) !== rentAmount ? " (prorated)" : ""}</span><span>{money(monthRent(month))}</span></div>
                          ))}
                          <div className="d-flex justify-content-between"><span className="text-muted">Service fee ({RENT_PAYMENT_METHODS[payMethod].feeLabel})</span><span>{money(fee)}</span></div>
                          <div className="d-flex justify-content-between fw-semibold border-top pt-1"><span>Total due</span><span>{money(total)}</span></div>
                        </div>
                      );
                    })()}
                    {!stripePromise ? (
                      <Alert color="warning" className="py-2 small mb-0">
                        Online payments aren't configured yet. Please contact your property owner.
                      </Alert>
                    ) : payIntentLoading || !clientSecret ? (
                      <div className="text-center py-4"><Spinner size="sm" color="primary" /></div>
                    ) : (
                      <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                        <RentPaymentForm
                          amountLabel={money(payAmountCents != null ? payAmountCents / 100 : subtotal)}
                          payerEmail={user?.email}
                          onSuccess={handlePaySuccess}
                          onError={setPayError}
                          onSubmittingChange={setPaySubmitting}
                        />
                      </Elements>
                    )}
                  </>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {paySuccess ? (
              <Button color="success" outline onClick={() => setPayModal(false)}>Done</Button>
            ) : (
              <Button color="secondary" outline onClick={() => setPayModal(false)} disabled={paySubmitting}>Cancel</Button>
            )}
          </ModalFooter>
        </Modal>

      </Container>
    </div>
  );
};

export default TenantRentalDetail;
